import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Briefcase, Users, DollarSign, Code, Copy, RefreshCw } from 'lucide-react';

export const AgentSettings = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    client_notifications: true,
    commission_tracking: true,
    auto_deal_alerts: true,
    public_profile: true,
    client_limit: 50,
    commission_rate: 15,
  });

  const [agentCode, setAgentCode] = useState('ABC123XY');
  const [clients, setClients] = useState([
    { id: 1, name: 'John Athlete', sport: 'Basketball', status: 'active' },
    { id: 2, name: 'Jane Runner', sport: 'Track & Field', status: 'active' },
  ]);

  const updateSetting = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const generateNewCode = () => {
    const newCode = Math.random().toString(36).substring(2, 10).toUpperCase();
    setAgentCode(newCode);
    toast({
      title: 'New Agent Code Generated',
      description: 'Your new agent code has been created and is ready to share.',
    });
  };

  const copyAgentCode = () => {
    navigator.clipboard.writeText(agentCode);
    toast({
      title: 'Copied!',
      description: 'Agent code copied to clipboard.',
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Agent Code Management
          </CardTitle>
          <CardDescription>
            Share this code with athletes to establish agent-client relationships.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Your Agent Code</Label>
            <div className="flex gap-2">
              <Input value={agentCode} readOnly className="font-mono" />
              <Button variant="outline" size="icon" onClick={copyAgentCode}>
                <Copy className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={generateNewCode}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Athletes use this code to link their account with yours
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Client Management
          </CardTitle>
          <CardDescription>
            Manage your athlete clients and relationship settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="client-notifications">Client activity notifications</Label>
              <Switch
                id="client-notifications"
                checked={settings.client_notifications}
                onCheckedChange={(checked) => updateSetting('client_notifications', checked)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Maximum number of clients</Label>
              <Input
                type="number"
                value={settings.client_limit}
                onChange={(e) => updateSetting('client_limit', parseInt(e.target.value))}
                max={100}
                min={1}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Current Clients ({clients.length})</Label>
            <div className="space-y-2">
              {clients.map((client) => (
                <div key={client.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{client.name}</p>
                    <p className="text-sm text-muted-foreground">{client.sport}</p>
                  </div>
                  <Badge variant={client.status === 'active' ? 'default' : 'secondary'}>
                    {client.status}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Commission & Billing
          </CardTitle>
          <CardDescription>
            Manage commission rates and billing preferences.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="commission-tracking">Enable commission tracking</Label>
            <Switch
              id="commission-tracking"
              checked={settings.commission_tracking}
              onCheckedChange={(checked) => updateSetting('commission_tracking', checked)}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Default commission rate (%)</Label>
            <Input
              type="number"
              value={settings.commission_rate}
              onChange={(e) => updateSetting('commission_rate', parseInt(e.target.value))}
              max={50}
              min={1}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="auto-deal-alerts">Auto deal opportunity alerts</Label>
            <Switch
              id="auto-deal-alerts"
              checked={settings.auto_deal_alerts}
              onCheckedChange={(checked) => updateSetting('auto_deal_alerts', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Professional Profile
          </CardTitle>
          <CardDescription>
            Manage your public agent profile and credentials.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="public-profile">Make profile public</Label>
            <Switch
              id="public-profile"
              checked={settings.public_profile}
              onCheckedChange={(checked) => updateSetting('public_profile', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Button className="w-full">
        Save Agent Settings
      </Button>
    </div>
  );
};